import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useUser } from '../auth/useUser';
import { useGenerator } from '../features/generator/context/GeneratorContext';
import FinalizeView from '../features/generator/FinalizeView';
import SEO from '../components/SEO';

// Direct API call with token to avoid race condition
async function fetchLogo(getAccessTokenSilently, id) {
    const token = await getAccessTokenSilently();
    const response = await fetch(`/api/v1/logos/${id}`, {
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
    });

    if (!response.ok) {
        throw new Error('Failed to fetch icon');
    }

    return response.json();
}

export default function FinalizePage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { isAuthenticated, getAccessTokenSilently } = useAuth0();
    const { actions, state } = useGenerator();
    const { isLoading: isUserLoading } = useUser();
    const { getLogoById, handleDownloadBundle } = actions;
    const { error: contextError } = state;

    const [logo, setLogo] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        async function loadLogo() {
            // Wait for user identity to be fully established (Auth0 + Guest Claim)
            if (isUserLoading) return;

            setIsLoading(true);
            setError(null);

            // First try to get from context (current session)
            const found = getLogoById(id);
            if (found) {
                setLogo(found);
                setIsLoading(false);
                return;
            }

            // If not in context, fetch from API (for deep links / history)
            if (!isAuthenticated) {
                setError('Please sign in to view this icon');
                setIsLoading(false);
                return;
            }

            try {
                const fetched = await fetchLogo(getAccessTokenSilently, id);
                setLogo(fetched);
            } catch (err) {
                console.error('Failed to load icon:', err);
                setError('Icon not found');
            } finally {
                setIsLoading(false);
            }
        }

        loadLogo();
    }, [id, getLogoById, isAuthenticated, getAccessTokenSilently, isUserLoading]);

    if (isLoading) {
        return (
            <main className="flex-1 app-scroll">
                <div className="container-page py-6 h-full flex items-center justify-center">
                    <div className="text-center">
                        <div className="animate-spin w-10 h-10 border-3 border-violet-400 border-t-transparent rounded-full mx-auto mb-4" />
                        <p className="text-slate-600 dark:text-slate-400">Loading icon...</p>
                    </div>
                </div>
            </main>
        );
    }

    if (error || !logo) {
        return (
            <main className="flex-1 app-scroll">
                <div className="container-page py-6 h-full flex items-center justify-center">
                    <div className="glass p-8 text-center max-w-md">
                        <div className="w-16 h-16 mx-auto mb-4 bg-red-500/10 rounded-2xl flex items-center justify-center border border-red-400/20">
                            <svg className="w-8 h-8 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                        </div>
                        <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100 mb-2">Icon not found</h2>
                        <p className="text-slate-600 dark:text-slate-400 mb-4">This icon may have been deleted or doesn't exist.</p>
                        <button onClick={() => navigate('/')} className="btn-primary px-6 py-2">
                            Go to Generator
                        </button>
                    </div>
                </div>
            </main>
        );
    }

    return (
        <main className="flex-1 app-scroll">
            <SEO
                title="Download Icon"
                description="Download your AI-generated icon in multiple formats and sizes."
                noindex={true}
            />
            <div className="container-page py-6 h-full">
                {contextError && (
                    <div className="mb-4 alert-error">
                        <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                        <span>{contextError}</span>
                        <button
                            onClick={() => actions.setError(null)}
                            className="ml-auto btn-ghost px-2 py-1 text-red-100 hover:bg-white/5 focus:ring-red-400/60"
                        >
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                )}
                <div className="glass overflow-hidden h-[calc(100vh-140px)] p-4">
                    <FinalizeView
                        logo={logo}
                        onBack={() => navigate(-1)}
                        onDownload={handleDownloadBundle}
                    />
                </div>
            </div>
        </main>
    );
}
